<template>
  <div class="gs1-details">
    <div class="content mt-8 ml-3 gs1-content">
      <div class="label font-weight-bold" v-if="isGS1Code">
        {{$t('gs1_digital_link_url')}}
      </div>
      <div class="label font-weight-bold" v-else>
        {{$t('default_url')}}
      </div>

      <div class="url-preview py-3 mono">
        {{urlPreview}}
        <v-tooltip bottom content-class="copy-url-tooltip">
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon v-bind="attrs" v-on="{
                ...on,
                click: copyUrlToClipboard
              }">
              <v-icon color="primary">mdi-content-copy</v-icon>
            </v-btn>
          </template>
          <span class="black--text body-2">{{ $t('copy_url') }}</span>
        </v-tooltip>
      </div>

      <v-form ref="gs1-form" v-if="isGS1Code || isUpgradingToGs1" v-model="isGs1CodeFormValid" class="pt-0 pb-8">
        <div class="label mb-5 font-weight-bold">{{$t('primary_keys')}}</div>
        <v-row class="ma-0">
          <v-col md="6" class="mr-3">
            <v-text-field
              small
              dense
              outlined
              :label="`(01) ${$t('gtin')}*`"
              :hint="$t('gs1_gtin_hint')"
              :counter="14"
              :rules="gtinRules"
              :error-messages="gtinErrorMessages"
              required
              v-model="form.gs1_gtin"
              :disabled="isGs1FormLocked"
              validate-on-blur />
          </v-col>
        </v-row>

        <div class="label my-3 font-weight-bold">{{$t('key_qualifiers')}}</div>
          <v-row class="ma-0">
            <!-- CPV field -->
            <v-col cols="12" md="6">
              <v-text-field
                :label="`(22) ${$t('gs1_cpv')}`"
                :hint="$t('gs1_cpv_hint')"
                small
                dense
                outlined
                persistent-hint
                :counter="20"
                v-model="form.gs1_cpv"
                :rules="fieldRules"
                :required="false"
                :disabled="isGs1FormLocked"
                validate-on-blur />
            </v-col>

            <!-- lot field -->
            <v-col cols="12" md="6">
              <v-text-field
                small
                dense
                outlined
                :label="`(10) ${$t('gs1_lot')}`"
                :hint="$t('gs1_lot_batch_number')"
                persistent-hint
                :counter="20"
                :rules="fieldRules"
                v-model="form.gs1_lot"
                :required="false"
                :disabled="isGs1FormLocked"
                validate-on-blur>
                <template v-slot:append>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-bind="attrs" v-on="{
                          ...on,
                          click: handleRefreshIconClick
                        }" :disabled="isGs1FormLocked">
                        mdi-refresh
                      </v-icon>
                    </template>
                    <span class="black--text body-2">{{ $t('gs1_generate_batch_number') }}</span>
                  </v-tooltip>
                </template>
              </v-text-field>
            </v-col>

            <!-- serial number -->
            <v-col cols="12" md="6">
              <v-text-field
                small
                dense
                outlined
                :label="`(21) ${$t('gs1_serial_number')}`"
                :hint="$t('gs1_serial_number_hint')"
                persistent-hint
                :counter="20"
                :rules="fieldRules"
                v-model="form.gs1_serial"
                :required="false"
                :disabled="isGs1FormLocked"
                validate-on-blur>
              </v-text-field>
            </v-col>
          </v-row>

          <div class="d-flex flex-column align-start mt-4 mb-3">
            <div class="label mb-1 font-weight-bold">{{$t('qrcode_gs1_data_attributes')}}</div>
            <div class="mb-4">{{$t('qrcode_data_attributes_caption')}}</div>
            <v-btn v-if="dataAttributes.length < dataAttributeOptions.length" :disabled="isGs1FormLocked" @click="addDataAttribute()" color="primary" class="my-1 pa-2" text large>
              <v-icon class="mr-2" color="primary">mdi-plus-circle</v-icon>
              {{ $t('qrcode_add_data_attributes') }}
            </v-btn>
          </div>
          <v-row class="ma-0" v-for="(attribute, index) of dataAttributes" :key="index">
            <v-col cols="12" md="6">
              <v-select
                small
                dense
                outlined
                v-model="attribute.key"
                :items="dataAttributeOptions"
                item-text="text"
                item-value="value"
                :rules="dataAttributeKeyRules(attribute)"
                :disabled="isGs1FormLocked"
              />
            </v-col>
            <v-col cols="12" md="5">
              <v-text-field
                small
                dense
                outlined
                persistent-hint
                :counter="2"
                v-model="attribute.value"
                :rules="dataAttributeValueRules(attribute)"
                :disabled="isGs1FormLocked"
              />
            </v-col>
            <v-col cols="auto">
              <v-btn :disabled="isGs1FormLocked" color="red" @click="removeDataAttribute(index)" icon><v-icon>mdi-close-circle</v-icon></v-btn>
            </v-col>
          </v-row>
      </v-form>
    </div>
    <div class="actions-ctn">
      <v-btn color="primary" @click="upgradeToGs1" raised v-if="!isGS1Code && !isUpgradingToGs1">
        <v-icon>mdi-plus</v-icon>
        {{$t('create_gs1_digital_link')}}
      </v-btn>

      <v-btn class="mr-2" v-if="isUpgradingToGs1 || (!isGs1FormLocked && isGS1Code)" @click="cancel()" text outlined color="primary">
        {{$t('cancel')}}
      </v-btn>

      <v-btn class="mr-2" v-if="isGs1FormLocked && isGS1Code" @click="openUnlockModalConfirmation()" text outlined color="#F22A42">
        <v-icon small class="mr-2">mdi-lock-open-outline</v-icon>
        {{$t('unlock_editing')}}
      </v-btn>

      <v-btn @click="save()" v-show="isGS1Code || isUpgradingToGs1" :disabled="saving || !hasUnsavedChanges" color="primary">
        {{$t('save')}}
      </v-btn>
    </div>

    <v-dialog width="450" v-model="unlockModal">
      <v-card scrollable>
      <v-card-title class="text-break">
        {{$t('unlock_modal_title')}}
      </v-card-title>
      <v-row class="pa-5 ma-0">
        <v-col class="py-0">
          <div class="red--text font-weight-bold" >{{$t('unlock_modal_decription_warning')}}</div>
          <div class="mt-3">
            {{$t('unlock_modal_decription_1')}}<br><br>
            {{$t('unlock_modal_decription_2')}}
          </div>
        </v-col>
      </v-row>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="unlockModal = false">{{$t('cancel')}}</v-btn>
        <v-btn color="primary" @click="unlockForm()">{{$t('unlock_editing')}}</v-btn>
      </v-card-actions>
    </v-card>
    </v-dialog>
  </div>
</template>

<script>

import Utils from '@/utils/utils'
import { mapGetters, mapMutations } from 'vuex'

export default {
  name: 'ProductDetailsGs1',
  data () {
    return {
      saving: false,
      unlockModal: false,
      isGs1CodeFormValid: false,
      formSubmitted: false,
      form: {
        gs1_gtin: '',
        gs1_lot: '',
        gs1_cpv: '',
        gs1_serial: '',
        gs1_data_attributes: ''
      },
      dataAttributes: [],
      initialForm: {},
      initialDataAttributes: []
    }
  },
  computed: {
    ...mapGetters(['systemPrefix', 'isGs1FormLocked', 'isUpgradingToGs1', 'hasCodeRenameError', 'codeRenameErrorMsg', 'selectedProduct', 'hasUnsavedChanges']),
    isGS1Code () {
      return Utils.isGS1Identifier(this.selectedProduct.code?.message)
    },
    urlPreview () {
      return `${this.systemPrefix}${this.selectedProduct.code?.message}${this.selectedProduct.code?.scm_data.gs1_data_attributes ? `?${this.selectedProduct.code.scm_data.gs1_data_attributes}` : ''}`
    },
    gs1Code () {
      const segments = []
      if (this.form.gs1_gtin) segments.push(`${Utils.AI.GTIN}/${this.form.gs1_gtin}`)
      if (this.form.gs1_cpv) segments.push(`${Utils.AI.CPV}/${this.form.gs1_cpv}`)
      if (this.form.gs1_lot) segments.push(`${Utils.AI.LOT_BATCH_NUMBER}/${this.form.gs1_lot}`)
      if (this.form.gs1_serial) segments.push(`${Utils.AI.SERIAL_NUMBER}/${this.form.gs1_serial}`)
      return segments.join('/')
    },
    gtinRules () {
      return [
        v => !!v || this.$t('gs1_gtin_required'),
        v => /^[0-9]+$/.test(v) || this.$t('gs1_field_numeric_allowed'),
        v => v.length === 14 || this.$t('gs1_gtin_fourteen_characters')
        // v => this.formSubmitted ? this.checkGtin(v) : true
      ]
    },
    gtinErrorMessages () {
      if (!this.hasCodeRenameError) return []
      return this.codeRenameErrorMsg
        .map(e => e.key === 'scm.code_rename.exists' ? this.$t('scm.code_rename.exists') : (e?.message || ''))
        .filter(Boolean)
    },
    serialRules () {
      return [
        v => !v || v.length >= 2 || this.$t('minimum_characters_required', { count: 2 }),
        v => !v || v.length <= 20 || this.$t('maximum_characters_allowed', { count: 20 }),
        v => !v || /^[a-zA-Z0-9"._!+,*[\]';:=@$()><-]+$/.test(v) || this.$t('qrcode_field_serial_number_characters_allowed')
      ]
    },
    fieldRules () {
      return [
        v => !v || Utils.isAlphaNumeric(v) || this.$t('gs1_field_numeric_alpha_allowed'),
        v => !v || v.length <= 20 || this.$t('maximum_characters_allowed', { count: 20 })
      ]
    },
    dataAttributeOptions () {
      return [
        { text: `(20) ${this.$t('qrcode_data_attribute_20')}`, value: 20 }
      ]
    },

    dataAttributesStr () {
      const validDataAttributes = this.dataAttributes.filter(attribute => attribute.key && attribute.value)
      return validDataAttributes.map(attribute => `${attribute.key}=${attribute.value}`).join('&')
    }
  },
  watch: {
    form: {
      deep: true,
      handler (newVal) {
        this.setUnsavedChangesStatus(!Utils.deepCompare(newVal, this.initialForm))
      }
    },
    dataAttributes: {
      deep: true,
      handler (newVal) {
        this.form.gs1_data_attributes = this.dataAttributesStr
      }
    }
  },
  methods: {

    checkGtin (gtin) {
      return false
    },

    ...mapMutations({
      setGs1FormLockStatus: 'SET_GS1_FORM_LOCK_STATUS',
      setIsUpgradingToGs1: 'SET_UPGRADING_TO_GS1_STATUS',
      setUnsavedChangesStatus: 'SET_UNSAVED_CHANGES_STATUS'
    }),

    copyUrlToClipboard () {
      navigator.clipboard.writeText(this.urlPreview).then(() => {
        this.$store.dispatch('showSnackBar', { message: this.$t('url_copied_to_clipboard') })
      })
    },

    resetForm () {
      this.form = { ...this.initialForm }
      this.dataAttributes = JSON.parse(JSON.stringify(this.initialDataAttributes))
    },

    cancel () {
      this.resetForm()
      this.setIsUpgradingToGs1(false)
      this.setGs1FormLockStatus(true)
      this.setUnsavedChangesStatus(false)
    },

    upgradeToGs1 () {
      this.setIsUpgradingToGs1(true)
      this.setGs1FormLockStatus(false)
    },

    async save () {
      const isValid = await this.$refs['gs1-form'].validate()
      if (!isValid) {
        return
      }
      if (this.selectedProduct.code.message === this.gs1Code && this.selectedProduct.code.scm_data.gs1_data_attributes === this.form.gs1_data_attributes) {
        this.setGs1FormLockStatus(true)
        this.setIsUpgradingToGs1(false)
        return
      }
      this.saving = true
      try {
        if (this.selectedProduct.code.message === this.gs1Code && this.selectedProduct.code.scm_data.gs1_data_attributes !== this.form.gs1_data_attributes) {
          await this.$store.dispatch('updateTags', { code: this.selectedProduct.code, tags: this.form, productId: this.selectedProduct.id })
        } else {
          await this.$store.dispatch('renameCode', { product: this.selectedProduct, source: this.selectedProduct.code, gs1Id: this.gs1Code, tags: this.form })
        }
        this.setGs1FormLockStatus(true)
        this.setIsUpgradingToGs1(false)
        this.setUnsavedChangesStatus(false)
        this.initialForm = JSON.parse(JSON.stringify(this.form))
        this.initialDataAttributes = JSON.parse(JSON.stringify(this.dataAttributes))
        this.$store.dispatch('showSnackBar', { message: this.$t('saved_successfully') })
        this.saving = false
      } catch (e) {
        console.log('error renaming code', e)
        this.saving = false
      }
    },

    unlockForm () {
      this.unlockModal = false
      this.setGs1FormLockStatus(false)
    },

    openUnlockModalConfirmation () {
      this.unlockModal = true
    },

    handleRefreshIconClick () {
      this.form.gs1_lot = Utils.generateLotNumber()
    },

    initGs1Form () {
      const matchPattern = /^01\/(\d{14})(\/22\/([a-zA-Z0-9]+))?(\/10\/([a-zA-Z0-9]+))?/
      const message = matchPattern.exec(this.selectedProduct.code.message)

      if (message) {
        this.form.gs1_gtin = message[1]
        if (message[3]) this.form.gs1_cpv = message[3]
        if (message[5]) this.form.gs1_lot = message[5]
        if (message[7]) this.form.gs1_serial = message[7]
      }

      const attributesStr = this.selectedProduct.code?.scm_data.gs1_data_attributes || ''
      const attributePairs = attributesStr.split('&').filter(pair => pair !== '')
      this.form.gs1_data_attributes = attributesStr
      this.dataAttributes = attributePairs.map(pair => {
        const [keyStr, value] = pair.split('=')
        return {
          key: parseInt(keyStr),
          value
        }
      })
      this.initialForm = JSON.parse(JSON.stringify(this.form))
      this.initialDataAttributes = JSON.parse(JSON.stringify(this.dataAttributes))
    },

    addDataAttribute () {
      this.dataAttributes.push({ key: '', value: '' })
    },
    removeDataAttribute (index) {
      this.dataAttributes.splice(index, 1)
    },

    dataAttributeKeyRules (attribute) {
      return [
        v => !attribute.value || !!v || this.$t('qrcode_select_key_if_value')
      ]
    },
    dataAttributeValueRules (attribute) {
      return [
        v => !attribute.key || !!v || this.$t('qrcode_enter_value_if_key'),
        v => !v || Utils.isNumeric(v) || this.$t('qrcode_field_numeric_allowed'),
        v => !v || v.length === 2 || this.$t('qrcode_field_two_numbers')
      ]
    },
    onDiscardUnsavedChanges () {
      if (this.isGS1Code) {
        this.initGs1Form()
      } else {
        this.form = { gs1_gtin: '', gs1_lot: '', gs1_cpv: '', gs1_serial: '', gs1_data_attributes: '' }
        this.dataAttributes = []
        this.initialForm = JSON.parse(JSON.stringify(this.form))
      }
      this.$nextTick(() => this.setUnsavedChangesStatus(false))
    }
  },
  mounted () {
    // Vuex-backed flags leak between products when the tab is re-mounted for
    // a different product; reset them to per-product defaults on every mount.
    this.setIsUpgradingToGs1(false)
    if (this.isGS1Code) {
      this.setGs1FormLockStatus(true)
      this.initGs1Form()
    } else {
      this.setGs1FormLockStatus(false)
    }
    window.addEventListener('discard-unsaved-changes', this.onDiscardUnsavedChanges)
  },
  beforeDestroy () {
    window.removeEventListener('discard-unsaved-changes', this.onDiscardUnsavedChanges)
  }
}

</script>

<style lang="sass" scoped>

@import '@/variables'

.gs1-details
  display: flex
  flex-direction: column
  height: 100%
  min-height: 0

.gs1-content
  flex: 1 1 auto
  overflow-y: auto
  padding-bottom: 8px
  min-height: 0

.actions-ctn
  position: sticky
  bottom: 0
  background: white
  padding: 16px 24px
  text-align: left
  border-top: 1px solid #eee
  z-index: 5
  display: flex
  flex-wrap: wrap

@media (max-width: 960px)
  .actions-ctn
    position: fixed
    left: 0
    right: 0
    bottom: 0
    padding: 12px 16px
    z-index: 20
  .gs1-content
    padding-bottom: 80px

.error-messages
  color: $red

.url-preview
  color: $primary

.gs1-status
  border-radius: 4px
  border: 1px solid #999
  font-size: 16px
  letter-spacing: 0.8px
  padding: 2px 6px
  &.created
    color: #3FC135
    border-color: #3FC135

</style>

<style lang="sass">
// Vuetify tooltips are portaled to body, so scoped styles cannot reach them.
// Override the global `.v-tooltip__content { opacity: 0 }` rule (variables.sass)
// only for this specific tooltip, keyed by its content-class.
.v-tooltip__content.copy-url-tooltip
  opacity: 0.9 !important
</style>
